import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { wembiiSupportApiService } from "./services/api";
import {
  SupportChatAppearance,
  SupportConversation,
} from "./interfaces/types";
import {
  addSupportMessageAction,
  setSupportConfigAction,
  setSupportConversationsAction,
  setSupportMessagesAction,
  setSupportVisitorsAction,
  setSelectedSupportConversationAction,
  setWidgetSessionAction,
  setSupportOnlineAgentsAction,
  setSupportAgentSocketSubscriptionAction,
  upsertSupportConversationAction,
  upsertSupportVisitorAction,
} from "./store/actions";
import {
  subscribeWembiiSupportAgentAction,
  subscribeWembiiSupportClientAction,
  sendWembiiSupportMessageAction,
  disconnectWembiiSupportClientAction,
} from "./store/socket-actions";
import {
  clearSupportConversationId,
  persistSupportConversationId,
} from "./supportSessionStorage";
import {
  supportConfigSelector,
  supportConversationsListSelector,
  selectedSupportConversationSelector,
  supportMessagesSelector,
  supportVisitorsListSelector,
  widgetSessionSelector,
  supportOnlineAgentsSelector,
} from "./store/selector";

export function useWembiiSupportCore() {
  const dispatch = useDispatch();
  const config = useSelector(supportConfigSelector);
  const conversations = useSelector(supportConversationsListSelector);
  const selected = useSelector(selectedSupportConversationSelector);
  const messages = useSelector(supportMessagesSelector);
  const visitors = useSelector(supportVisitorsListSelector);
  const widgetSession = useSelector(widgetSessionSelector);
  const onlineAgents = useSelector(supportOnlineAgentsSelector);

  const loadConfig = useCallback(async () => {
    const data = await wembiiSupportApiService.getConfig();
    dispatch(setSupportConfigAction({ config: data }));
    return data;
  }, [dispatch]);

  const saveConfig = useCallback(
    async (payload: Partial<SupportChatAppearance>) => {
      const data = await wembiiSupportApiService.updateConfig(payload);
      dispatch(setSupportConfigAction({ config: data }));
      return data;
    },
    [dispatch]
  );

  const uploadConfigAvatar = useCallback(
    async (file: Blob) => {
      const data = await wembiiSupportApiService.postAvatar(file);
      dispatch(setSupportConfigAction({ config: data }));
      return data;
    },
    [dispatch]
  );

  const loadConversations = useCallback(
    async (params?: { status?: string; search?: string }) => {
      const data = await wembiiSupportApiService.getConversations(params);
      dispatch(setSupportConversationsAction({ conversations: data }));
      return data;
    },
    [dispatch]
  );

  const loadConversationDetail = useCallback(async (id: string) => {
    return wembiiSupportApiService.getConversation(id);
  }, []);

  const selectConversation = useCallback(
    (id: string | null) => {
      dispatch(setSelectedSupportConversationAction({ id }));
    },
    [dispatch]
  );

  const loadMessages = useCallback(
    async (conversationId: string) => {
      const data = await wembiiSupportApiService.getMessages(conversationId);
      dispatch(setSupportMessagesAction({ messages: data }));
      return data;
    },
    [dispatch]
  );

  const loadVisitors = useCallback(async () => {
    const data = await wembiiSupportApiService.getVisitors();
    dispatch(setSupportVisitorsAction({ visitors: data }));
    return data;
  }, [dispatch]);

  const loadOnlineAgents = useCallback(async () => {
    const agents = await wembiiSupportApiService.getOnlineAgents();
    dispatch(setSupportOnlineAgentsAction({ agents }));
    return agents;
  }, [dispatch]);

  const initWidgetSession = useCallback(
    async (companyId: string, metadata: Record<string, unknown>) => {
      const session = await wembiiSupportApiService.createSession(
        companyId,
        metadata
      );
      dispatch(
        setWidgetSessionAction({
          conversationId: String(session.conversationId),
          room: session.room,
        })
      );
      persistSupportConversationId(String(session.conversationId));
      if (session.config) {
        dispatch(setSupportConfigAction({ config: session.config }));
      }
      if (session.onlineAgents) {
        dispatch(setSupportOnlineAgentsAction({ agents: session.onlineAgents }));
      }
      return session;
    },
    [dispatch]
  );

  const endWidgetSession = useCallback(
    async (conversationId: string) => {
      dispatch(disconnectWembiiSupportClientAction({ conversationId }));
      try {
        await wembiiSupportApiService.endSession(conversationId);
      } catch {
        /* best effort */
      }
      clearSupportConversationId();
      dispatch(
        setWidgetSessionAction({
          conversationId: null,
          room: null,
        })
      );
    },
    [dispatch]
  );

  const disconnectSupportClient = useCallback(
    (conversationId: string) => {
      dispatch(disconnectWembiiSupportClientAction({ conversationId }));
    },
    [dispatch]
  );

  const subscribeAsAgent = useCallback(
    (userId: string, conversation?: SupportConversation | null) => {
      dispatch(
        setSupportAgentSocketSubscriptionAction({
          userId,
          conversationId: conversation?.id,
          room: conversation?.room,
        })
      );
      dispatch(
        subscribeWembiiSupportAgentAction({
          userId,
          conversationId: conversation?.id,
          room: conversation?.room,
        })
      );
    },
    [dispatch]
  );

  const subscribeAsClient = useCallback(
    (userId: string, conversationId: string, room: string) => {
      dispatch(
        subscribeWembiiSupportClientAction({
          userId,
          conversationId,
          room,
        })
      );
    },
    [dispatch]
  );

  const sendMessage = useCallback(
    (
      conversationId: string,
      content: string,
      senderType: "user" | "agent",
      senderId: string,
      consultationLabel?: string
    ) => {
      dispatch(
        sendWembiiSupportMessageAction({
          conversationId,
          content,
          senderType,
          senderId,
          consultationLabel,
        })
      );
    },
    [dispatch]
  );

  const updateConversationStatus = useCallback(
    async (conversationId: string, status: string) => {
      return wembiiSupportApiService.updateStatus(conversationId, status);
    },
    []
  );

  return {
    config,
    conversations,
    selected,
    messages,
    visitors,
    widgetSession,
    onlineAgents,
    loadConfig,
    saveConfig,
    uploadConfigAvatar,
    loadConversations,
    loadConversationDetail,
    selectConversation,
    loadMessages,
    loadVisitors,
    loadOnlineAgents,
    initWidgetSession,
    endWidgetSession,
    disconnectSupportClient,
    subscribeAsAgent,
    subscribeAsClient,
    sendMessage,
    updateConversationStatus,
  };
}

export type { SupportChatAppearance, SupportConversation, SupportVisitor } from "./interfaces/types";
